import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import TenantContext, { DEFAULT_TENANT_CONFIG } from './TenantContext';

// ─── Font registry ────────────────────────────────────────────────────────────
// All 5 families must be pre-loaded in public/index.html for zero jank.
const FONT_REGISTRY = {
  'plus-jakarta-sans':  "'Plus Jakarta Sans', sans-serif",
  'cormorant-garamond': "'Cormorant Garamond', serif",
  'playfair-display':   "'Playfair Display', serif",
  'dm-sans':            "'DM Sans', sans-serif",
  'epilogue':           "'Epilogue', sans-serif",
  'josefin-sans':       "'Josefin Sans', sans-serif",
};

// ─── Hex → CSS rgb triplet ────────────────────────────────────────────────────
function hexToRgb(hex) {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

// ─── Apply theme to document ──────────────────────────────────────────────────
function applyTheme(theme) {
  const root = document.documentElement;
  root.style.setProperty('--color-primary',     theme.primaryColor);
  root.style.setProperty('--color-primary-rgb', hexToRgb(theme.primaryColor));
  root.style.setProperty('--color-dark-bg',     theme.darkBg);
  root.style.setProperty('--color-dark-bg-rgb', hexToRgb(theme.darkBg));
  root.style.setProperty('--color-light-bg',    theme.lightBg);

  const fontValue = FONT_REGISTRY[theme.fontKey] || FONT_REGISTRY['plus-jakarta-sans'];
  root.style.setProperty('--font-display', fontValue);

  const fontBodyValue = FONT_REGISTRY[theme.fontBodyKey] || FONT_REGISTRY['plus-jakarta-sans'];
  root.style.setProperty('--font-body', fontBodyValue);

  // colorMode: tenant-locked light or dark. null = leave it to WeddingApp / user toggle.
  if (theme.colorMode === 'light') {
    root.classList.remove('dark');
    // --color-fg drives all light-mode text overrides in index.html.
    // Dark slate so text is readable on the parchment background.
    root.style.setProperty('--color-fg',     '#1e293b');
    root.style.setProperty('--color-fg-rgb', '30, 41, 59');
  } else if (theme.colorMode === 'dark') {
    root.classList.add('dark');
    root.style.setProperty('--color-fg',     '#ffffff');
    root.style.setProperty('--color-fg-rgb', '255, 255, 255');
  }
  // null: leave --color-fg at its :root default (#ffffff) — WeddingApp owns the class.

  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) metaTheme.setAttribute('content', theme.darkBg);
}

// ─── Extract weddingId from URL ───────────────────────────────────────────────
// /jake-emily/... → 'jake-emily'
// /              → null (backwards compat — falls back to default config + original Firestore paths)
// Reserved paths that are NOT weddingIds:
const RESERVED_PATHS = new Set(['admin', 'static', 'favicon.ico', 'manifest.json', 'robots.txt']);

function getWeddingIdFromUrl() {
  const segment = window.location.pathname.split('/').filter(Boolean)[0];
  if (!segment || RESERVED_PATHS.has(segment)) return null;
  return segment;
}

// ─── TenantProvider ───────────────────────────────────────────────────────────
export default function TenantProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_TENANT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const weddingId = getWeddingIdFromUrl();

    if (!weddingId) {
      // Root path or reserved path — use defaults, apply default theme, done.
      applyTheme(DEFAULT_TENANT_CONFIG.theme);
      document.title = DEFAULT_TENANT_CONFIG.appName;
      setLoading(false);
      return;
    }

    // Fetch tenant config from Firestore (direct read, not via storage.js)
    // Config lives at weddings/{weddingId} — even-segment document path.
    const configRef = doc(db, 'weddings', weddingId);
    getDoc(configRef)
      .then((snap) => {
        if (!snap.exists()) {
          // Unknown weddingId — use defaults so app still renders
          console.warn(`[TenantProvider] No config found for weddingId="${weddingId}". Using defaults.`);
          applyTheme(DEFAULT_TENANT_CONFIG.theme);
          document.title = DEFAULT_TENANT_CONFIG.appName;
          setConfig({ ...DEFAULT_TENANT_CONFIG, weddingId });
        } else {
          // Deep-merge fetched config over defaults so missing fields degrade gracefully
          const remote = snap.data();
          const merged = {
            ...DEFAULT_TENANT_CONFIG,
            ...remote,
            theme:       { ...DEFAULT_TENANT_CONFIG.theme,       ...(remote.theme       || {}) },
            events:      { ...DEFAULT_TENANT_CONFIG.events,      ...(remote.events      || {}) },
            features:    { ...DEFAULT_TENANT_CONFIG.features,    ...(remote.features    || {}) },
            story:       { ...DEFAULT_TENANT_CONFIG.story,       ...(remote.story       || {}) },
            leaderboard: { ...DEFAULT_TENANT_CONFIG.leaderboard, ...(remote.leaderboard || {}) },
            settings:    { ...DEFAULT_TENANT_CONFIG.settings,    ...(remote.settings    || {}) },
          };
          applyTheme(merged.theme);
          document.title = merged.appName;
          setConfig(merged);
        }
      })
      .catch((err) => {
        console.error('[TenantProvider] Config fetch failed:', err);
        applyTheme(DEFAULT_TENANT_CONFIG.theme);
        setConfig({ ...DEFAULT_TENANT_CONFIG, weddingId });
      })
      .finally(() => setLoading(false));
  }, []);

  // Hold rendering until config is ready so children never see stale defaults
  // flash before the theme is applied. The loading UI is handled by App.js's
  // own isLoading gate, so we just render null here — it's a brief fetch.
  if (loading) return null;

  return (
    <TenantContext.Provider value={config}>
      {children}
    </TenantContext.Provider>
  );
}
